/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Tariffs And Services Content Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.39.0
 * @since 1.4.0
 */

import { Fragment, ReactElement, useCallback, useEffect, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageProducts,
} from '@modules/internationalization/intSelectors';
import {
    IPageProductsChangeModalTranslation,
    IPageProductsTranslation,
} from '@modules/internationalization/types/pages/products';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';
import ProductCardCampaignTariff from '@pages/PageProducts/components/ProductCards/ProductCardCampaignTariff';
import { RatePlanCard2025 } from '@pages/PageProducts/components/ProductCards/RatePlanCard2025/RatePlanCard2025';
import {
    CampaignRateplan2025ItemStyled,
} from '@pages/PageProducts/components/ProductCards/RatePlanCard2025/styled/RatePlanCard2025Styled';
import { IRootReducer } from '@reducers/rootReducer';

import { ICommonTranslation } from '../../../modules/internationalization/types';
import {
    setProductsItemsSelectedAction,
    setProductsTariffSelectedAction,
    setTariffAndServicesTariffSetRangeAction,
    setTariffsAndServicesItemSelected,
} from '../tariffsAndServicesActions';
import { EProductsCart, EProductsCategory, EProductsTab } from '../tariffsAndServicesConstants';
import {
    ICardTariffNewItem,
    IProductCardTariff,
    IProductCardTariffSetRange,
    IProductCardTariffsRangeSelected,
} from '../tariffsAndServicesProductTariffTypes';
import { TProductItem } from '../tariffsAndServicesProductTypes';
import { IProductOfferingsMap } from '../tariffsAndServicesReducer';
import {
    makeSelectProductsCart,
    makeSelectProductsCategoryIds,
    makeSelectProductsCategorySelected,
    makeSelectProductsItemsSelected,
    makeSelectProductsMeta,
    makeSelectProductsProductsListSelected,
    makeSelectProductsTabSelected,
    makeSelectProductsTariffSelected,
    makeSelectProductsTariffsRange,
    makeSelectRoamingOptionsSelected,
    makeSelectTariffsAndServicesCategoryWithServicesList,
    makeSelectTariffsAndServicesFoundItems,
    makeSelectTariffsAndServicesProductOfferingsMap,
} from '../tariffsAndServicesSelectors';
import {
    IProductCategoryIds,
    IProductsMeta,
    IProductsSelected,
    IProductsSetItemSelectedPayload,
    IProductsSetItemsSelectedPayload,
    IProductsWithCategory,
    IProductsWithCategoryList,
    IRoamingOption,
} from '../tariffsAndServicesTypes';
import ProductCardService from './ProductCards/ProductCardService';
import ProductCardTariff from './ProductCards/ProductCardTariff';
import ProductContentCards from './ProductContentCards';
import ProductContentInfo from './ProductContentInfo';
import ProductsChangeModal from './ProductsChangeModal';
import { RoamingOptionCard } from './RoamingOptions/RoamingOptionCard';
import { ProductsCardsItemStyled, ProductsCardsStyled } from './styled/ProductsContentStyled';


export type TProductsContent = IConnectedProps & IDispatchProps;

export const ProductsContent = ({
    openModal,
    setServiceItemSelected,
    setServiceItemsSelected,
    setTariffItemSelected,
    setTariffRange,

    categorySelected,
    foundItems,
    itemsSelected,
    productCard,
    productMeta,
    productOfferingsMap,
    servicesCategoryList,
    tabSelected,
    tariffSelected,
    tariffsSelectedRange,
    translation,
    translationCommon,
    productsListSelected,
    isMegaPowersFeatureEnabled,
    productsCategoryIds,
    roamingOptionsSelected,
    isRoamingProductsFeatureEnabled,
}: TProductsContent) => {

    const isCampaignRateplanCard2025 =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGN_RATEPLAN_CARD_2025));

    const [firstIndexTariffCardWithRange, setFirstIndexTariffCardWithRange] = useState<number>();
    const [isTariffRangeWasChanged, setTariffRangeWasChanged] = useState(false);

    /**
     * Для первой карточки тарифа с range нужно показывать тултип
     */
    useEffect(() => {
        const productList = servicesCategoryList[productMeta.tariffsCategoryId]?.productList;
        if (productList) {
            setFirstIndexTariffCardWithRange(
                productList.findIndex(
                    (product: IProductCardTariff | ICardTariffNewItem) => product.ranges && product.ranges.length > 1,
                ),
            );
        }
    }, [servicesCategoryList, productMeta.tariffsCategoryId]);

    const onSetTariffRange = useCallback(
        (payload: IProductCardTariffSetRange) => {
            setTariffRange(payload);
            setTariffRangeWasChanged(true);
        },
        [setTariffRange],
    );

    /*
     * -------------------------------------------
     * Обработка клика по карточке
     * -------------------------------------------
     */

    const openChangeCartModal = useCallback(
        (onAgree: () => void, translation: IPageProductsChangeModalTranslation) => {
            openModal({
                render: (props: IModalRenderProps) => (
                    <ProductsChangeModal
                        translation={translation}
                        onAgree={() => {
                            props.onCloseModal();
                            setServiceItemsSelected({ itemsSelectedAvailable: [] });
                            setTariffItemSelected({ id: 0 });
                            onAgree();
                        }}
                        onDisagree={() => props.onCloseModal()}
                    />
                ),
            });
        },
        [openModal, setServiceItemsSelected, setTariffItemSelected],
    );

    const onSelectServiceCard = useCallback(
        (item: TProductItem) => {
            if (tabSelected === EProductsTab.CONNECTED) {
                setServiceItemSelected({ id: item.id });
            } else {
                if (!item.available) return;

                const currentCart =
                    productMeta.campaignServicesCategoryId === categorySelected
                        ? EProductsCart.CAMPAIGN
                        : EProductsCart.PUBLIC;

                const serviceItemSelected: IProductsSetItemSelectedPayload = { cart: currentCart, id: item.id };
                if (item.personalOfferingId) {
                    serviceItemSelected.personalOfferingId = item.personalOfferingId;
                }
                if (item.limitConnections) {
                    serviceItemSelected.limitConnections = item.limitConnections;
                    serviceItemSelected.connected = item.connected;
                }

                if (productCard !== currentCart && (itemsSelected.length || tariffSelected.id)) {
                    openChangeCartModal(
                        () => setServiceItemSelected(serviceItemSelected),
                        currentCart === EProductsCart.CAMPAIGN
                            ? translation.cartChangeToCampaignModal
                            : translation.cartChangeToPublicModal,
                    );
                    return;
                }
                setServiceItemSelected(serviceItemSelected);
            }
        },
        [
            categorySelected,
            productMeta,
            itemsSelected.length,
            openChangeCartModal,
            productCard,
            setServiceItemSelected,
            tabSelected,
            tariffSelected,
            translation,
        ],
    );

    const onSelectTariffCard = useCallback(
        (item: IProductCardTariff | ICardTariffNewItem) => {
            console.log('1111 onSelectTariffCard item', item);
            const currentCart =
                productMeta.campaignTariffsCategoryId === categorySelected
                    ? EProductsCart.CAMPAIGN
                    : EProductsCart.PUBLIC;

            const tariffItemSelected: IProductsSetItemSelectedPayload = { cart: currentCart, id: item.id };
            if (item.personalOfferingId) {
                tariffItemSelected.personalOfferingId = item.personalOfferingId;
            }
            if (item.limitConnections) {
                tariffItemSelected.limitConnections = item.limitConnections;
                tariffItemSelected.connected = item.connected;
            }
            if (productCard !== currentCart && tariffSelected.id) {
                openChangeCartModal(
                    () => setTariffItemSelected(tariffItemSelected),
                    currentCart === EProductsCart.CAMPAIGN
                        ? translation.cartChangeToCampaignModal
                        : translation.cartChangeToPublicModal,
                );
            } else {
                setTariffItemSelected(tariffItemSelected);
            }

            // if tariff range was not changed - set middle range value
            if (!tariffsSelectedRange[item.id] && item.ranges && item.ranges.length) {
                setTariffRange({
                    id: item.id,
                    rangeId: item.ranges[Math.floor(item.ranges.length / 2)].id,
                });
            }
        },
        [
            categorySelected,
            openChangeCartModal,
            productCard,
            productMeta,
            setTariffItemSelected,
            setTariffRange,
            tariffSelected,
            tariffsSelectedRange,
            translation,
        ],
    );

    /*
     * -------------------------------------------
     *           Render component
     * -------------------------------------------
     */

    const renderProductCard = (
        servicesCategoryList: IProductsWithCategoryList,
        renderCard: (item: TProductItem, index: number) => ReactElement,
    ) =>
        Object.values(servicesCategoryList).map((serviceCategory: IProductsWithCategory) => (
            <ProductContentCards
                categorySelected={categorySelected}
                isConnected={tabSelected === EProductsTab.CONNECTED}
                key={serviceCategory.category.id}
                noResultsFilter={translation.search.noResultsFilter}
                productList={serviceCategory.productList}
                productMeta={productMeta}
                renderCard={renderCard}
                title={serviceCategory.category.title}
            />
        ));

    const renderServicesCard = (item: TProductItem) => (
        <Fragment key={item.id}>
            <ProductsCardsItemStyled
                isSelected={
                    itemsSelected.filter(
                        (selected) =>
                            item.id === selected.id && item.personalOfferingId === selected.personalOfferingId,
                    ).length > 0
                }
                onClick={() => onSelectServiceCard(item)}
            >
                <ProductCardService
                    {...item}
                    {...productOfferingsMap[item.id]}
                    isConnected={tabSelected === EProductsTab.CONNECTED}
                    isMegaPowers={isMegaPowersFeatureEnabled && productMeta.megaPowersCategoryId === categorySelected}
                    translation={translation}
                    translationCommon={translationCommon}
                />
            </ProductsCardsItemStyled>
        </Fragment>
    );

    const { tariffsCartegoryIds } = productsCategoryIds;

    switch (categorySelected) {
        case EProductsCategory.SEARCH:
            return (
                <Fragment>
                    <ProductContentInfo changeRegionInSubsMobile={translation.regionBlock.search} />
                    <ProductContentCards
                        categorySelected={EProductsCategory.SEARCH}
                        noResultsFilter={translation.search.noSearchMatcher}
                        productList={foundItems}
                        productMeta={productMeta}
                        renderCard={renderServicesCard}
                        title={translation.search.searchResultsTitle}
                    />
                </Fragment>
            );
        case productMeta.servicesCategoryId: // EProductsCategory.SERVICES
            return (
                <ProductContentCards
                    categorySelected={productMeta.servicesCategoryId}
                    productList={productsListSelected}
                    productMeta={productMeta}
                    renderCard={renderServicesCard}
                    title={translation.search.searchResultsTitle}
                />
            );
        case tariffsCartegoryIds.find((el) => el === categorySelected):
            return (
                <Fragment>
                    <ProductContentInfo changeRegionInSubsMobile={translation.regionBlock.tariffs} />
                    {renderProductCard(servicesCategoryList, (item: TProductItem, index: number) => (
                        <ProductsCardsItemStyled
                            isNewCard
                            isSelected={item.id === tariffSelected.id}
                            key={item.id}
                            onClick={() => onSelectTariffCard(item)}
                        >
                            <ProductCardTariff
                                {...item}
                                pageProductTranslation={translation}
                                setRange={onSetTariffRange}
                                showRangeTooltip={!isTariffRangeWasChanged && index === firstIndexTariffCardWithRange}
                            />
                        </ProductsCardsItemStyled>
                    ))}
                </Fragment>
            );
        case productMeta.campaignTariffsCategoryId:
            return (
                <Fragment>
                    <ProductContentInfo changeRegionInSubsMobile={translation.regionBlock.tariffs} />
                    {renderProductCard(servicesCategoryList, (item: TProductItem) => (
                            isCampaignRateplanCard2025 ?
                            <CampaignRateplan2025ItemStyled
                                isSelected={
                                    item.id === tariffSelected.id &&
                                    (!tariffSelected.personalOfferingId ||
                                        item.personalOfferingId === tariffSelected.personalOfferingId)
                                }
                                key={`${item.id}-${item.personalOfferingId}`}
                                onClick={() => onSelectTariffCard(item)}
                            >
                                <RatePlanCard2025
                                    connected={(item as IProductCardTariff).connected}
                                    internetLimited={(item as IProductCardTariff).internetLimited}
                                    limitConnections={(item as IProductCardTariff).limitConnections}
                                    rateplanName={item.title}
                                    roamings={(item as IProductCardTariff).roamings}
                                    totalPrice={(item as IProductCardTariff).payment?.value}
                                />
                            </CampaignRateplan2025ItemStyled> :
                            <ProductsCardsItemStyled
                                isSelected={
                                    item.id === tariffSelected.id &&
                                    (!tariffSelected.personalOfferingId ||
                                        item.personalOfferingId === tariffSelected.personalOfferingId)
                                }
                                key={`${item.id}-${item.personalOfferingId}`}
                                onClick={() => onSelectTariffCard(item)}
                            >
                                <ProductCardCampaignTariff {...(item as IProductCardTariff)} />
                            </ProductsCardsItemStyled>
                    ))}
                </Fragment>
            );
        case EProductsCategory.SELECTED:
            return (
                <Fragment>
                    <ProductContentInfo changeRegionInSubsMobile={translation.regionBlock.services} />
                    {renderProductCard(servicesCategoryList, renderServicesCard)}
                    {isRoamingProductsFeatureEnabled &&
                        <ProductsCardsStyled productMeta={productMeta}>
                            {roamingOptionsSelected
                            .map(selectedOption =>
                                <RoamingOptionCard
                                    key={selectedOption.id}
                                    {...selectedOption}
                                />,
                            )}
                        </ProductsCardsStyled>
                    }
                </Fragment>
            );
        default:
            return (
                <Fragment>
                    <ProductContentInfo changeRegionInSubsMobile={translation.regionBlock.services} />
                    {renderProductCard(servicesCategoryList, renderServicesCard)}
                </Fragment>
            );
    }
};

interface IConnectedProps {
    productCard: EProductsCart;
    categorySelected: number;
    foundItems: TProductItem[];
    itemsSelected: IProductsSelected[];
    productMeta: IProductsMeta;
    productOfferingsMap: IProductOfferingsMap;
    productsListSelected: TProductItem[];
    servicesCategoryList: IProductsWithCategoryList;
    tariffSelected: IProductsSelected;
    tariffsSelectedRange: IProductCardTariffsRangeSelected;

    tabSelected: EProductsTab;

    translation: IPageProductsTranslation;
    translationCommon: ICommonTranslation;

    isMegaPowersFeatureEnabled: boolean;
    productsCategoryIds: IProductCategoryIds;
    roamingOptionsSelected: IRoamingOption[];
    isRoamingProductsFeatureEnabled: boolean;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    productCard: makeSelectProductsCart(),
    categorySelected: makeSelectProductsCategorySelected(),
    foundItems: makeSelectTariffsAndServicesFoundItems(),
    itemsSelected: makeSelectProductsItemsSelected(),
    productMeta: makeSelectProductsMeta(),
    productOfferingsMap: makeSelectTariffsAndServicesProductOfferingsMap(),
    productsListSelected: makeSelectProductsProductsListSelected(),
    servicesCategoryList: makeSelectTariffsAndServicesCategoryWithServicesList(true),
    tariffSelected: makeSelectProductsTariffSelected(),
    tariffsSelectedRange: makeSelectProductsTariffsRange(),

    tabSelected: makeSelectProductsTabSelected(),

    translation: makeSelectTranslationPageProducts(),
    translationCommon: makeSelectTranslationCommon(),
    isMegaPowersFeatureEnabled: makeSelectFeatureEnabled(FeatureNameEnum.MEGA_POWERS),
    productsCategoryIds: makeSelectProductsCategoryIds(),
    roamingOptionsSelected: makeSelectRoamingOptionsSelected(),
    isRoamingProductsFeatureEnabled: makeSelectFeatureEnabled(FeatureNameEnum.ROAMING_PRODUCTS),
});

interface IDispatchProps {
    openModal: (payload: IModalOpenPayload) => void;
    setServiceItemSelected: (payload: IProductsSetItemSelectedPayload) => void;
    setServiceItemsSelected: (payload: IProductsSetItemsSelectedPayload) => void;
    setTariffItemSelected: (payload: IProductsSetItemSelectedPayload) => void;
    setTariffRange: (payload: IProductCardTariffSetRange) => void;
    setTariffSelected: (payload: IProductsSetItemSelectedPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    setServiceItemSelected: (payload) => dispatch(setTariffsAndServicesItemSelected(payload)),
    setServiceItemsSelected: (payload: IProductsSetItemsSelectedPayload) =>
        dispatch(setProductsItemsSelectedAction(payload)),
    setTariffItemSelected: (payload: IProductsSetItemSelectedPayload) =>
        dispatch(setProductsTariffSelectedAction(payload)),
    setTariffRange: (payload) => dispatch(setTariffAndServicesTariffSetRangeAction(payload)),
    setTariffSelected: (payload: IProductsSetItemSelectedPayload) => dispatch(setProductsTariffSelectedAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsContent);
